import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@sluitstraat/core';
import { inboedelStyles as styles } from '../../../styles/commonStyles';
import { InfoTooltip } from '../../../components/InfoTooltip';
import { getEc, InboedelCoverageDescriptor } from './coverages';

// One optional-coverage toggle card (label + info popup + premium badge + toggle switch +
// optional amount input / Smart Home sum select), moved verbatim from InboedelScreen where
// this block appeared six times. Card-specific bits (labels, PP section, sub-input, max-amount
// error key) come from the INBOEDEL_OPTIONAL_COVERAGES descriptor in coverages.ts.
//
// Premium badge rules preserved per card:
// - Smart Home (EC 5470): premium read from the EC array entry (EC_TTOT gate, EC_BTP badge).
// - GP/LS/OP/HU/KB: section TTOT gate, sectionDisplayPrice(<S>_BTP) badge.
//
// The toggles deliberately do NOT reset lastCalculatedData: coverage selections are excluded
// from the auto-calc signature (prices are pre-calculated). The amount inputs DO reset it once
// the value is in range so the auto-calc effect re-prices on blur.

interface CoverageToggleCardProps {
  descriptor: InboedelCoverageDescriptor;
  data: any;
  previewPp: any;
  sectionDisplayPrice: (btp: number | string | undefined, section: string, sec?: any) => number;
  handleCustomFieldChange: (fieldPath: string, value: any) => void;
  handleSmarthomeSumChange?: (value: number) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  validationErrors: Record<string, string>;
  lastCalculatedData: React.MutableRefObject<string>;
  formatCurrency: (value: number | string) => string;
  unformatCurrency: (value: string) => number | null;
  infofolioFilledFields?: Set<string>;
}

const readPath = (obj: any, path: string) => path.split('.').reduce((o: any, k: string) => o?.[k], obj);

export const CoverageToggleCard: React.FC<CoverageToggleCardProps> = ({
  descriptor,
  data,
  previewPp,
  sectionDisplayPrice,
  handleCustomFieldChange,
  handleSmarthomeSumChange,
  setValidationErrors,
  validationErrors,
  lastCalculatedData,
  formatCurrency,
  unformatCurrency,
  infofolioFilledFields,
}) => {
  const { t } = useTranslation();
  const { key, dekkingenKey } = descriptor;
  // Narrowed once so the amount fields stay non-optional inside the input callbacks below.
  const amountDescriptor = descriptor.kind === 'amount' ? descriptor : null;
  const amountLocked = Boolean(
    amountDescriptor?.lockFieldKey && infofolioFilledFields?.has(amountDescriptor.lockFieldKey),
  );

  const pp = data?.Contractdocument?.PP;
  const selected = data?.Supportsection?.Dekkingen?.[dekkingenKey] === 'J';

  let premiumVisible: boolean;
  let premiumValue: number;
  if (descriptor.ecCode) {
    const ec = getEc(pp, descriptor.ecCode);
    const previewEc = getEc(previewPp, descriptor.ecCode);
    premiumVisible = Number(ec?.EC_TTOT ?? previewEc?.EC_TTOT) > 0;
    premiumValue = sectionDisplayPrice(ec?.EC_BTP ?? previewEc?.EC_BTP, 'EC', ec ?? previewEc);
  } else {
    const section = descriptor.ppSection ? pp?.[descriptor.ppSection] : undefined;
    const preview = descriptor.ppSection ? previewPp?.[descriptor.ppSection] : undefined;
    premiumVisible = (section?.[`${descriptor.ppSection}_TTOT`] ?? preview?.[`${descriptor.ppSection}_TTOT`]) > 0;
    premiumValue = sectionDisplayPrice(
      section?.[`${descriptor.ppSection}_BTP`] ?? preview?.[`${descriptor.ppSection}_BTP`],
      descriptor.ppSection as string,
      section ?? preview,
    );
  }

  const amountValue = amountDescriptor ? readPath(data, amountDescriptor.amountFieldPath) : undefined;

  const labelAtoms: { text: string; space: boolean }[] = [];
  t(descriptor.labelKey).trim().split(/\s+/).forEach((word, wi) => {
    word.split(/(?=belang|sieraden)/i)
      .filter(Boolean)
      .forEach((part, pi) => labelAtoms.push({ text: part, space: wi > 0 && pi === 0 }));
  });

  const cardError = descriptor.errorFieldPath ? validationErrors[descriptor.errorFieldPath] : undefined;

  return (
    <div id={descriptor.errorAnchorId} style={styles.toggleRow}>
      <div style={styles.colFull}>
        <div style={styles.rowBetweenFull}>
          <div style={styles.toggleLabelCol}>
            <label style={styles.toggleLabel}>
              {labelAtoms.map((atom, i) => {
                const sep = i === 0 ? null : (atom.space ? ' ' : <wbr />);
                if (i < labelAtoms.length - 1) return <React.Fragment key={i}>{sep}{atom.text}</React.Fragment>;
                return (
                  <React.Fragment key={i}>
                    {sep}
                    <span style={styles.labelTailNoWrap}>
                      {atom.text}
                      <InfoTooltip
                        title={t(`screens.inboedel.coverageInfo.${key}.title`)}
                        items={Array.from({ length: descriptor.infoItemCount }, (_, i2) => ({
                          text: t(`screens.inboedel.coverageInfo.${key}.item${i2 + 1}`),
                          html: descriptor.infoHtmlItems?.includes(i2 + 1),
                        }))}
                        popupClassName="info-tooltip-popup-card"
                        wrapperStyle={styles.infoButtonWrapper}
                      />
                    </span>
                  </React.Fragment>
                );
              })}
            </label>
          </div>
          {premiumVisible && (
            <span style={{
              ...styles.coveragePremiumBadge,
              ...styles.mr12,
              ...(!selected ? styles.coveragePremiumBadgePending : {})
            }}>
              + €{formatPrice(premiumValue)}
            </span>
          )}
          <div
            onClick={() => {
              const newValue = data?.Supportsection?.Dekkingen?.[dekkingenKey] === 'J' ? 'N' : 'J';
              handleCustomFieldChange(`Supportsection.Dekkingen.${dekkingenKey}`, newValue);
              // No default VERZSOM — leave empty for placeholder to show
            }}
            style={{
              ...styles.toggleSwitch,
              ...(selected ? styles.toggleSwitchOn : styles.toggleSwitchOff)
            }}
          >
            <div style={{
              ...styles.toggleSlider,
              ...(selected ? styles.toggleSliderOn : styles.toggleSliderOff)
            }} />
          </div>
        </div>
        {cardError && <p style={styles.errorMessage}>{cardError}</p>}

        {/* Coverage Amount Input - shown when coverage is enabled */}
        {amountDescriptor && selected && (
          <div style={styles.dividedSection}>
            <label style={styles.fieldLabelBlock} htmlFor={amountDescriptor.amountInputId}>
              {t(amountDescriptor.amountLabelKey ?? 'screens.inboedel.fields.coverageAmount')}
            </label>
            <div style={styles.currencyInputContainer}>
              <span style={styles.currencySymbol}>€</span>
              <input
                id={amountDescriptor.amountInputId}
                type="text"
                placeholder={t(amountDescriptor.amountPlaceholderKey ?? 'screens.inboedel.aboveAmountPlaceholder')}
                value={amountValue ? formatCurrency(amountValue) : ''}
                disabled={amountLocked}
                onChange={(e) => {
                  const value = unformatCurrency(e.target.value) ?? 0;
                  handleCustomFieldChange(amountDescriptor.amountFieldPath, value);
                  // Only show max error during typing, min error on blur
                  if (amountDescriptor.amountMax !== undefined && value > amountDescriptor.amountMax) {
                    setValidationErrors(prev => ({ ...prev, [amountDescriptor.amountFieldPath]: t(amountDescriptor.maxErrorKey) }));
                  } else {
                    setValidationErrors(prev => { const { [amountDescriptor.amountFieldPath]: _, ...rest } = prev; return rest; });
                  }
                  if (amountDescriptor.recalcMin !== undefined
                    && value >= amountDescriptor.recalcMin && value <= amountDescriptor.amountMax) {
                    // Invalidate signature to trigger auto-calculation useEffect
                    lastCalculatedData.current = '';
                  }
                }}
                style={{
                  ...styles.currencyInput,
                  ...(validationErrors[amountDescriptor.amountFieldPath] ? styles.fieldInputError : {}),
                  ...(amountLocked ? styles.fieldInputDisabled : {})
                }}
                inputMode="numeric"
              />
            </div>
            {validationErrors[amountDescriptor.amountFieldPath] && (
              <p style={styles.errorMessage}>{validationErrors[amountDescriptor.amountFieldPath]}</p>
            )}
          </div>
        )}

        {/* Smart Home insured-sum selector — only relevant when the coverage is selected */}
        {descriptor.kind === 'sumSelect' && descriptor.ecCode && selected && (
          <div style={styles.dividedSection}>
            <label style={styles.fieldLabelBlock} htmlFor="smarthome-amount">
              {t('screens.inboedel.fields.coverageAmount')}
            </label>
            <select
              id="smarthome-amount"
              value={String(getEc(data?.Contractdocument?.PP, descriptor.ecCode)?.EC_VERZSOM ?? 3000)}
              onChange={(e) => handleSmarthomeSumChange?.(Number(e.target.value))}
              style={styles.fieldSelect}
            >
              <option value="3000">€ {formatCurrency(3000)}</option>
              <option value="6000">€ {formatCurrency(6000)}</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};
